// /for-pharma — Big Pharma BD & In-Licensors audience page.
// Mature, high-level positioning. JCA-readiness baked into every dossier as differentiation.
// Anchored by an anonymized EU specialty pharma case study.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import {
  ArrowRight, Building2, Target, Globe2, ShieldCheck, BarChart3, Layers,
  Calendar, Handshake, CheckCircle2, Sparkles, Brain, FileText, Stethoscope, ExternalLink
} from 'lucide-react';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el); return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const OFFERINGS = [
  {
    badge: 'Flagship',
    badgeColor: '#70C24C',
    icon: <Layers className="w-5 h-5" />,
    title: 'BD Retainer — 12-month productized engagement',
    desc: 'Replaces five vendors with one team: subscription database, conference scout, KOL network, diligence consultant, and deal facilitator. Calibrated to one geography of your choice.',
    bullets: [
      '90+ structured deliverables per year',
      '20–30 asset dossiers, 8-dimension scoring',
      '4 conferences covered live with on-site BD',
      'Dedicated platform copilot & dashboard',
    ],
    cta: { label: 'See the full retainer', to: '/services/pharma-bd-retainer' },
  },
  {
    badge: 'New · Q1 2026',
    badgeColor: '#A88BFF',
    icon: <Sparkles className="w-5 h-5" />,
    title: 'Committee Intelligence Workspace — bimonthly cohorts on retainer',
    desc: 'A year-long retainer that turns in-licensing into a rigorous, repeatable committee process. Every two months we deliver a fresh cohort of pre-qualified assets — AI-enriched, scored across 9 scientific domains, and validated by your internal committee in a blinded Delphi review inside our private client portal.',
    bullets: [
      'Bi-monthly cohorts of 25–40 in-scope assets per cycle',
      '9-domain Scientific Evaluation + ForesightRx Opportunity Score (/50 composite)',
      'Blinded Delphi scoring across your 6-role committee (Medical · Clinical · Regulatory · Pharmacovigilance · BD · Research)',
      'Math-derived final verdict (In-License · Reassess · Decline) with AI rationale on every disagreement',
      'Synthesized Executive Summary auto-generated once all reviewers submit',
      'Private branded client portal (deep-dive modal, reference library, audit trail)',
    ],
    cta: { label: 'Request a working demo', to: '/apply?intent=committee_intelligence' },
  },
  {
    badge: 'Targeted',
    badgeColor: '#00709B',
    icon: <Target className="w-5 h-5" />,
    title: 'Buyer-Specific Asset Shortlists',
    desc: 'Sharply scoped, deck-aligned shortlists for a single corporate-development thesis — EU commercialization-ready, China-origin pipeline, rare-disease bolt-ons. Deterministic scoring + live web enrichment per asset.',
    bullets: [
      'Mirrors your BD deck criteria (sub-buckets, weights, exclusions)',
      'Per-asset Perplexity-sourced enrichment with citations',
      'JCA / AMCP readiness pre-flag on each asset',
      'Delivered as a branded PPTX or live workspace',
    ],
    cta: { label: 'Request a tailored shortlist', to: '/apply?intent=pharma_shortlist' },
  },
  {
    badge: 'Conferences',
    badgeColor: '#A88BFF',
    icon: <Calendar className="w-5 h-5" />,
    title: 'Conference BD Command',
    desc: 'Pre-event target lists, counterpart dossiers, scheduled meetings, and structured follow-up. For BIO International, BIO-Europe, ChinaBio, CPHI, JPM, and beyond. We are on the ground — so your BD team meets only qualified counterparties.',
    bullets: [
      'Pre-conference prioritized target list',
      'Meeting dossiers — science + deal history + signals',
      'Same-day flash reports from the floor',
      'Structured 5-day post-conference follow-up',
    ],
    cta: { label: 'See upcoming conferences', to: '/platform#conferences' },
  },
];

const WHY_WIN = [
  { icon: <Brain />, title: '8-Dimension Asset Scoring', desc: 'Clinical, regulatory, IP, competitive, commercial, strategic fit, deal readiness, and risk. Same yardstick on every dossier.' },
  { icon: <Globe2 />, title: 'EU JCA / HTA Pre-Read', desc: 'Every in-scope oncology, ATMP, or cell & gene asset is pre-screened for EU Joint Clinical Assessment readiness. You see the payer story before you bid.' },
  { icon: <Stethoscope />, title: 'US AMCP / Payer-Value Pre-Read', desc: 'For US-bound assets: AMCP dossier readiness, ICER signal, RWE plan, and likely payer objections — surfaced at diligence stage, not after.' },
  { icon: <ShieldCheck />, title: 'Living Intelligence, Not a Database', desc: 'Continuously refreshed from 12+ global conferences per year. What you read today is current — not a snapshot from a quarterly data dump.' },
];

export default function ForPharmaPage() {
  useSEO({
    title: 'For Pharma BD — Cross-Border Deal Flow, Fully De-Risked | ForesightRx',
    description: 'Big Pharma BD partner: 12-month retainer, buyer-specific asset shortlists, on-the-ground conference BD. JCA & AMCP readiness built into every dossier.',
    canonical: 'https://foresightrx.com/for-pharma',
  });
  usePageTracker('for-pharma');

  const [heroRef, heroInView] = useInView(0);
  const [offerRef, offerInView] = useInView();
  const [whyRef, whyInView] = useInView();

  return (
    <div className="min-h-screen bg-[#0A1128] copy-protect" data-testid="for-pharma-page">
      <PublicNav />

      {/* HERO */}
      <section ref={heroRef} className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute top-1/3 -left-32 w-[600px] h-[600px] rounded-full bg-[#00709B]/15 blur-[120px] pulse-glow" />
        <div className="absolute bottom-0 -right-20 w-[400px] h-[400px] rounded-full bg-[#70C24C]/10 blur-[100px]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className={heroInView ? 'reveal-up' : 'opacity-0'}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00709B]/10 border border-[#00709B]/30 mb-7">
              <Building2 className="w-3.5 h-3.5 text-[#00709B]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00709B]">For Pharma BD & In-Licensors</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05] max-w-4xl" data-testid="for-pharma-hero-heading">
              Cross-border deal flow,
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#70C24C] to-[#00709B]">fully de-risked.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-[#A1A8C3] font-light max-w-3xl leading-relaxed">
              We don't hand you a database and wish you luck. Every asset we surface comes scored across
              eight dimensions, pre-screened for <strong className="text-white">EU JCA readiness</strong> and
              <strong className="text-white"> US AMCP / payer-value readiness</strong>, and de-risked before it
              reaches your deal committee. The result: shorter diligence cycles, fewer surprises, sharper offers.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/apply?intent=pharma_retainer"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-sm font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
                    data-testid="for-pharma-cta-proposal">
                Request a proposal <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/case-studies"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/15 text-white text-sm font-medium hover:bg-white/5 transition-all duration-300"
                    data-testid="for-pharma-cta-case-studies">
                See case studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* OFFERINGS */}
      <section ref={offerRef} className="py-24 lg:py-32 bg-[#0E1733]" data-testid="for-pharma-offerings">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-3xl mb-16 ${offerInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Four ways to engage</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Continuous retainer. Committee intelligence. Sharp shortlists. On-the-ground BD.
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 lg:gap-6">
            {OFFERINGS.map((o, i) => (
              <div key={i}
                   className={`bg-gradient-to-br from-[#111A3A] to-[#0A1128] border border-white/5 rounded-3xl p-7 lg:p-8 hover:border-[#00709B]/40 transition-all duration-500 flex flex-col ${offerInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}
                   data-testid={`for-pharma-offering-${i}`}>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-2xl bg-[#00709B]/15 flex items-center justify-center text-[#00709B]">
                    {o.icon}
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${o.badgeColor}15`, color: o.badgeColor }}>
                    {o.badge}
                  </span>
                </div>
                <h3 className="text-lg lg:text-xl font-bold text-white mb-3">{o.title}</h3>
                <p className="text-sm text-[#A1A8C3] font-light leading-relaxed mb-5">{o.desc}</p>
                <div className="space-y-2.5 mb-7 flex-1">
                  {o.bullets.map((b, j) => (
                    <div key={j} className="flex items-start gap-2.5">
                      <CheckCircle2 className="mt-0.5 w-4 h-4 text-[#70C24C] shrink-0" />
                      <span className="text-xs text-[#A1A8C3]">{b}</span>
                    </div>
                  ))}
                </div>
                <Link to={o.cta.to}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#00709B] hover:text-white transition-colors duration-300 group/link"
                      data-testid={`for-pharma-offering-cta-${i}`}>
                  {o.cta.label} <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY OUR SHORTLISTS WIN */}
      <section ref={whyRef} className="py-24 lg:py-32 bg-[#0A1128]" data-testid="for-pharma-why">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-3xl mb-16 ${whyInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Why our shortlists win</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              The payer story is in the dossier — before it's in the term sheet.
            </h2>
            <p className="mt-5 text-lg text-[#A1A8C3] font-light">
              Other BD partners hand you a list. We hand you a list that has already survived the
              evidence reviewer, the JCA assessor, and the payer committee — at least on paper.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_WIN.map((w, i) => (
              <div key={i}
                   className={`bg-[#111A3A] border border-white/5 rounded-2xl p-6 hover:border-[#70C24C]/30 transition-all ${whyInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}
                   data-testid={`for-pharma-why-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#70C24C]/10 flex items-center justify-center text-[#70C24C] mb-4 [&>svg]:w-5 [&>svg]:h-5">
                  {w.icon}
                </div>
                <h4 className="text-sm font-bold text-white mb-2">{w.title}</h4>
                <p className="text-xs text-[#A1A8C3] font-light leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>

          <div className={`mt-10 bg-gradient-to-r from-[#70C24C]/8 to-[#00709B]/8 border border-white/5 rounded-2xl p-7 ${whyInView ? 'reveal-up stagger-5' : 'opacity-0'}`}>
            <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">
              <strong className="text-white">JCA went live on 12 January 2025</strong> for all new oncology
              medicines and advanced therapy medicinal products in the EU. From 2028 it expands to orphan
              drugs; from 2030 to every centrally authorized product. Pharma BD teams that price JCA risk
              into diligence today are buying assets others will overpay for in eighteen months.
              <span className="text-[#70C24C] font-medium"> We've already built that lens.</span>
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#111A3A] relative overflow-hidden" data-testid="for-pharma-cta">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#00709B]/10 to-[#70C24C]/10 blur-[100px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6">
            Tell us your BD thesis. We'll bring the assets.
          </h2>
          <p className="text-lg text-[#A1A8C3] font-light mb-10 max-w-xl mx-auto">
            One geography. One thesis. We will scope a 12-month programme or a one-off shortlist —
            whichever fits your in-licensing rhythm.
          </p>
          <Link to="/apply?intent=pharma_retainer"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-base font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
                data-testid="for-pharma-bottom-cta">
            Request a tailored proposal <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-xs text-[#687399] uppercase tracking-wider">No commitment · Honest fit assessment</p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
